import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Badge, Button, confirmDialog } from "../../../components/ui";
import {
  carrierErrorCode,
  useAttachCarrierTracking,
  useCarrierTracking,
  useDetachCarrierTracking,
  useRefreshCarrierTracking,
  type CarrierEvent,
} from "../../../features/shipments/carrierTracking";
import type { Shipment } from "../../../features/shipments/types";
import { CARRIER_STATUS_TONES } from "../statusTones";
import CarrierTrackingFields, {
  EMPTY_CARRIER_DRAFT,
  isValidCarrierNumber,
  type CarrierTrackingDraft,
} from "./CarrierTrackingFields";

const NOT_CONFIGURED = "CARRIER_TRACKING_NOT_CONFIGURED";

/**
 * Tarjeta «Paquetería» del detalle del envío: estatus normalizado de 17TRACK,
 * último movimiento, línea de tiempo y acciones (actualizar ahora / quitar).
 * Sin guía, permite agregarla. Se refresca cada 60 s (useCarrierTracking).
 */
export default function CarrierTrackingCard({ shipment }: { shipment: Shipment }) {
  const { t, i18n } = useTranslation("envios");
  const query = useCarrierTracking(shipment.id);
  const attach = useAttachCarrierTracking();
  const refresh = useRefreshCarrierTracking();
  const detach = useDetachCarrierTracking();
  const [draft, setDraft] = useState<CarrierTrackingDraft>(EMPTY_CARRIER_DRAFT);
  const [draftError, setDraftError] = useState<string | null>(null);

  const errorMessage = (error: unknown) => {
    const code = carrierErrorCode(error);
    return t(`carrier.errors.${code ?? "generic"}`, { defaultValue: t("carrier.errors.generic") });
  };

  const formatDate = (value: string | null) => {
    if (!value) return "";
    const date = new Date(value);
    return Number.isNaN(date.getTime())
      ? value
      : new Intl.DateTimeFormat(i18n.language, {
          day: "2-digit",
          month: "short",
          hour: "2-digit",
          minute: "2-digit",
        }).format(date);
  };

  const record = query.data ?? null;
  // Mientras llega el detalle, el resumen espejado en el envío ya sirve.
  const summary = record ?? shipment.carrier_tracking ?? null;
  const notConfigured = carrierErrorCode(query.error) === NOT_CONFIGURED;

  const handleAttach = () => {
    const number = draft.number.replace(/\s+/g, "");
    if (!isValidCarrierNumber(number)) {
      setDraftError(t("carrier.invalidNumber"));
      return;
    }
    setDraftError(null);
    attach.mutate(
      { shipment_id: shipment.id, tracking_number: number, carrier_code: draft.carrierCode },
      {
        onSuccess: () => {
          setDraft(EMPTY_CARRIER_DRAFT);
          toast.success(t("carrier.added"));
        },
        onError: (error) => toast.error(errorMessage(error)),
      },
    );
  };

  const handleRefresh = () =>
    refresh.mutate(shipment.id, {
      onSuccess: () => toast.success(t("carrier.refreshed")),
      onError: (error) => toast.error(errorMessage(error)),
    });

  const handleRemove = async () => {
    if (!summary) return;
    const confirmed = await confirmDialog({
      title: t("carrier.removeTitle"),
      text: t("carrier.removeText", { number: summary.number }),
      confirmText: t("carrier.removeConfirm"),
      tone: "danger",
      icon: "warning",
    });
    if (!confirmed) return;
    detach.mutate(shipment.id, {
      onSuccess: () => toast.success(t("carrier.removed")),
      onError: (error) => toast.error(errorMessage(error)),
    });
  };

  const header = (
    <div className="mb-4 flex items-center justify-between gap-2">
      <div className="flex min-w-0 items-center gap-2">
        <i className="ri-truck-line text-lg text-content" />
        <h3 className="text-sm font-bold text-content">{t("carrier.title")}</h3>
      </div>
      {summary && (
        <Badge tone={CARRIER_STATUS_TONES[summary.status] ?? "neutral"} size="sm" dot>
          {t(`carrier.status.${summary.status}`, { defaultValue: summary.status })}
        </Badge>
      )}
    </div>
  );

  if (query.isLoading && !summary) {
    return (
      <div className="rounded-2xl border border-line p-5">
        {header}
        <div className="flex h-16 items-center justify-center">
          <i className="ri-loader-4-line animate-spin text-xl text-primary-500" />
        </div>
      </div>
    );
  }

  if (notConfigured) {
    return (
      <div className="rounded-2xl border border-line p-5">
        {header}
        <div className="flex items-center gap-3 rounded-lg bg-subtle p-3">
          <i className="ri-plug-line text-lg text-content-muted" />
          <p className="text-xs text-content-muted">{t("carrier.notConfigured")}</p>
        </div>
      </div>
    );
  }

  if (!summary) {
    return (
      <div className="rounded-2xl border border-line p-5">
        {header}
        <p className="mb-3 text-xs text-content-muted">{t("carrier.addHint")}</p>
        <div className="space-y-3">
          <CarrierTrackingFields
            value={draft}
            onChange={(next) => {
              setDraft(next);
              if (draftError) setDraftError(null);
            }}
            error={draftError}
            disabled={attach.isPending}
          />
          <Button
            size="sm"
            icon={<i className="ri-add-line" />}
            loading={attach.isPending}
            disabled={!draft.number.trim()}
            onClick={handleAttach}
          >
            {attach.isPending ? t("carrier.adding") : t("carrier.add")}
          </Button>
        </div>
      </div>
    );
  }

  const events: CarrierEvent[] = record?.events ?? (summary.last_event ? [summary.last_event] : []);

  return (
    <div className="rounded-2xl border border-line p-5">
      {header}

      <div className="space-y-1">
        <p className="truncate text-sm font-semibold text-content">
          {summary.carrier_name || t("carrier.unknownCarrier")}
        </p>
        <p className="truncate font-mono text-xs text-content-muted">{summary.number}</p>
        {summary.updated_at && (
          <p className="text-xs text-content-faint">
            {t("carrier.updatedAgo", { when: formatDate(summary.updated_at) })}
          </p>
        )}
      </div>

      <div className="mt-4 rounded-lg bg-subtle p-3">
        <p className="text-xs font-medium text-content-muted">{t("carrier.lastEvent")}</p>
        {summary.last_event ? (
          <>
            <p className="mt-1 text-sm text-content">{summary.last_event.description}</p>
            <p className="mt-0.5 text-xs text-content-muted">
              {[formatDate(summary.last_event.time), summary.last_event.location]
                .filter(Boolean)
                .join(" · ")}
            </p>
          </>
        ) : (
          <p className="mt-1 text-xs text-content-muted">{t("carrier.noEventsYet")}</p>
        )}
      </div>

      {events.length > 1 && (
        <div className="mt-4">
          <p className="mb-2 text-xs font-semibold text-content">{t("carrier.events")}</p>
          <div className="max-h-[320px] overflow-y-auto">
            {events.map((event, index) => (
              <div key={`${event.time}-${index}`} className="flex gap-2">
                <div className="relative flex flex-col items-center">
                  <div
                    className={`mt-1.5 h-2.5 w-2.5 rounded-full ${index === 0 ? "bg-primary-500" : "bg-line"}`}
                  />
                  {index < events.length - 1 && <div className="absolute top-5 h-full w-0.5 bg-line" />}
                </div>
                <div className="min-w-0 pb-3 pt-0.5">
                  <p className="text-xs text-content">{event.description}</p>
                  <p className="mt-0.5 text-xs text-content-muted">
                    {[formatDate(event.time), event.location].filter(Boolean).join(" · ")}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {query.isError && !notConfigured && (
        <p className="mt-3 text-xs text-error">{t("carrier.loadError")}</p>
      )}

      <div className="mt-4 flex gap-2 border-t border-line pt-4">
        <Button
          variant="outline"
          size="sm"
          className="flex-1"
          icon={<i className="ri-refresh-line" />}
          loading={refresh.isPending}
          onClick={handleRefresh}
        >
          {t("carrier.refresh")}
        </Button>
        <Button
          variant="dangerSoft"
          size="sm"
          className="flex-1"
          icon={<i className="ri-link-unlink" />}
          loading={detach.isPending}
          onClick={handleRemove}
        >
          {t("carrier.remove")}
        </Button>
      </div>
    </div>
  );
}
